/**
 * RubberBandSelection Component - Task Group 4
 *
 * Provides rubber-band (drag-box) selection for tools on the timeline.
 *
 * Features:
 * - Appears when user clicks and drags on empty timeline space
 * - Selection box follows mouse movement in both forward and backward directions
 * - Semi-transparent fill with dashed border
 * - Only activates when drag distance exceeds 5px threshold
 * - On drag end, selects all tools that intersect with selection rectangle
 * - Uses getBoundingClientRect() for accurate intersection detection
 *
 * Usage:
 * Rendered as a child of the scrollable tracks container in TimelineContainer.
 * Captures mouse events on an overlay that covers the tracks area.
 */

import { useState, useCallback, useEffect } from 'react';
import { useUiStore } from '../../stores/uiStore';

/**
 * Minimum drag distance in pixels before selection box appears
 * Prevents accidental selection on simple clicks
 */
const DRAG_THRESHOLD = 5;

/**
 * Selection box styling
 */
const SELECTION_BOX_STYLES = {
  /** Semi-transparent blue fill */
  FILL: 'rgba(59, 130, 246, 0.15)',
  /** Dashed border color using CSS variable */
  BORDER: '2px dashed var(--color-focus)',
} as const;

interface Point {
  x: number;
  y: number;
}

interface SelectionRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface RubberBandSelectionProps {
  /**
   * Ref to the container element for calculating relative positions
   * This should be the scrollable tracks container
   */
  containerRef: React.RefObject<HTMLDivElement | null>;

  /**
   * Optional callback when selection starts (e.g., to prevent other interactions)
   */
  onSelectionStart?: () => void;

  /**
   * Optional callback when selection ends
   */
  onSelectionEnd?: (selectedIds: string[]) => void;
}

/**
 * Calculate distance between two points
 */
function getDistance(p1: Point, p2: Point): number {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}

/**
 * Calculate selection rectangle from start and current points
 * Handles both forward and backward drag directions
 */
function calculateSelectionRect(start: Point, current: Point): SelectionRect {
  const left = Math.min(start.x, current.x);
  const top = Math.min(start.y, current.y);
  const width = Math.abs(current.x - start.x);
  const height = Math.abs(current.y - start.y);

  return { left, top, width, height };
}

/**
 * Check if two rectangles intersect
 * Uses the separating axis theorem for AABB collision detection
 */
function rectsIntersect(
  rect1: { left: number; top: number; right: number; bottom: number },
  rect2: { left: number; top: number; right: number; bottom: number }
): boolean {
  return !(
    rect1.right < rect2.left ||
    rect1.left > rect2.right ||
    rect1.bottom < rect2.top ||
    rect1.top > rect2.bottom
  );
}

/**
 * Find all tool IDs that intersect with the given selection rectangle
 * Uses document.querySelectorAll to find tool elements with data-tool-id attribute
 */
function findIntersectingToolIds(
  selectionRect: SelectionRect,
  containerRect: DOMRect
): string[] {
  const intersectingIds: string[] = [];

  // Get all tool elements in the document
  const toolElements = document.querySelectorAll('[data-tool-id]');

  // Convert selection rect to absolute coordinates (relative to viewport)
  const absoluteSelectionRect = {
    left: containerRect.left + selectionRect.left,
    top: containerRect.top + selectionRect.top,
    right: containerRect.left + selectionRect.left + selectionRect.width,
    bottom: containerRect.top + selectionRect.top + selectionRect.height,
  };

  toolElements.forEach((element) => {
    const toolId = element.getAttribute('data-tool-id');
    if (!toolId) return;

    const toolRect = element.getBoundingClientRect();
    const toolBounds = {
      left: toolRect.left,
      top: toolRect.top,
      right: toolRect.right,
      bottom: toolRect.bottom,
    };

    if (rectsIntersect(absoluteSelectionRect, toolBounds)) {
      intersectingIds.push(toolId);
    }
  });

  return intersectingIds;
}

export function RubberBandSelection({
  containerRef,
  onSelectionStart,
  onSelectionEnd,
}: RubberBandSelectionProps) {
  // Drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<Point | null>(null);
  const [dragCurrent, setDragCurrent] = useState<Point | null>(null);
  const [hasExceededThreshold, setHasExceededThreshold] = useState(false);

  // Get selection actions from uiStore
  const setSelection = useUiStore((state) => state.setSelection);
  const setRubberBandSelecting = useUiStore(
    (state) => state.setRubberBandSelecting
  );

  // NOTE: Removed useDragLayer hook - it was causing severe performance issues
  // by re-rendering on every DnD state change. We already handle dragstart
  // via a native DOM event listener (see handleDragStart below).

  /**
   * Update rubber-band selecting state when threshold is exceeded
   */
  useEffect(() => {
    setRubberBandSelecting(hasExceededThreshold);
  }, [hasExceededThreshold, setRubberBandSelecting]);

  /**
   * Handle mouse up - finalize selection
   * Defined first since it's used by the effect below
   */
  const handleMouseUp = useCallback(() => {
    if (!isDragging || !dragStart || !dragCurrent) {
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      setHasExceededThreshold(false);
      return;
    }

    // Only process selection if threshold was exceeded
    if (hasExceededThreshold) {
      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const selectionRect = calculateSelectionRect(dragStart, dragCurrent);
        const intersectingIds = findIntersectingToolIds(
          selectionRect,
          containerRect
        );

        // Only update selection if we found intersecting tools
        if (intersectingIds.length > 0) {
          setSelection(intersectingIds);
        }

        onSelectionEnd?.(intersectingIds);
      }
    }

    // Reset drag state
    setIsDragging(false);
    setDragStart(null);
    setDragCurrent(null);
    setHasExceededThreshold(false);
  }, [
    isDragging,
    dragStart,
    dragCurrent,
    hasExceededThreshold,
    containerRef,
    setSelection,
    onSelectionEnd,
  ]);

  /**
   * Set up event listeners on the container for rubber-band selection
   *
   * We listen on the container element directly (via ref) instead of using
   * an overlay div. This allows:
   * 1. Drag-drop to work (React DnD uses native drag events)
   * 2. Rubber-band selection to work (captures mousedown on empty space)
   *
   * The mousedown handler checks if the click was on a tool element and
   * ignores it if so - tools handle their own click/drag events.
   */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Check if click started on a tool element - if so, let tool handle it
      const target = e.target as HTMLElement;
      const toolElement = target.closest('[data-tool-id]');
      if (toolElement) return;

      // Check if click started on playhead handle - if so, let playhead handle it
      const playheadElement = target.closest('[data-testid="playhead-handle"]');
      if (playheadElement) return;

      // Don't start rubber-band if this is a right-click (context menu)
      if (e.button !== 0) return;

      // Get position relative to container
      const containerRect = container.getBoundingClientRect();
      const relativeX = e.clientX - containerRect.left;
      const relativeY = e.clientY - containerRect.top;

      setDragStart({ x: relativeX, y: relativeY });
      setDragCurrent({ x: relativeX, y: relativeY });
      setIsDragging(true);
      setHasExceededThreshold(false);
    };

    container.addEventListener('mousedown', handleMouseDown);
    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
    };
  }, [containerRef]);

  /**
   * Handle mouse move and mouse up via window listeners when dragging
   * This ensures we capture events even if mouse leaves the container
   */
  useEffect(() => {
    if (!isDragging) return;

    const container = containerRef.current;
    if (!container) return;

    const handleMouseMove = (e: MouseEvent) => {
      const containerRect = container.getBoundingClientRect();
      const relativeX = e.clientX - containerRect.left;
      const relativeY = e.clientY - containerRect.top;

      const newCurrent = { x: relativeX, y: relativeY };
      setDragCurrent(newCurrent);

      // Check if we've exceeded the drag threshold
      if (!hasExceededThreshold && dragStart) {
        const distance = getDistance(dragStart, newCurrent);
        if (distance > DRAG_THRESHOLD) {
          setHasExceededThreshold(true);
          onSelectionStart?.();
        }
      }
    };

    const handleGlobalMouseUp = () => {
      handleMouseUp();
    };

    /**
     * Handle drag start events from React DnD
     * When a DnD drag starts, we should cancel our rubber-band selection
     * to prevent conflicts between the two drag systems
     */
    const handleDragStart = () => {
      // Cancel rubber-band selection when React DnD drag starts
      setIsDragging(false);
      setDragStart(null);
      setDragCurrent(null);
      setHasExceededThreshold(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('dragstart', handleDragStart);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('dragstart', handleDragStart);
    };
  }, [
    isDragging,
    dragStart,
    hasExceededThreshold,
    containerRef,
    onSelectionStart,
    handleMouseUp,
  ]);

  // Calculate selection rectangle for rendering
  const selectionRect =
    dragStart && dragCurrent && hasExceededThreshold
      ? calculateSelectionRect(dragStart, dragCurrent)
      : null;

  return (
    <div
      className="absolute inset-0 z-20"
      style={{
        // Always transparent to pointer events - this overlay only renders the selection box
        // Mouse events are captured via useEffect on the container ref
        pointerEvents: 'none',
      }}
      data-testid="rubber-band-overlay"
    >
      {/* Selection box - only rendered when drag exceeds threshold */}
      {selectionRect && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${selectionRect.left}px`,
            top: `${selectionRect.top}px`,
            width: `${selectionRect.width}px`,
            height: `${selectionRect.height}px`,
            backgroundColor: SELECTION_BOX_STYLES.FILL,
            border: SELECTION_BOX_STYLES.BORDER,
            borderRadius: 'var(--radius-sm)',
          }}
          data-testid="rubber-band-selection-box"
        />
      )}
    </div>
  );
}
